import { Injectable, Injector, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { CredentialsTokensType, CustomersType, Exception, Token, TokenStatusType } from '@cargos/sprintpay-models';
import {
    AuthService,
    LoggerService,
    LoginAsCargoSprintUserResponse,
    LogoutResponse,
    TokenService,
} from '@cargos/sprintpay-services';
import dayjs from 'dayjs';
import {
    Observable,
    Subject,
    catchError,
    delay,
    distinctUntilChanged,
    forkJoin,
    map,
    of,
    retry,
    switchMap,
    take,
    takeUntil,
    throwError,
} from 'rxjs';
import Swal from 'sweetalert2';
import { ConfigService } from './config.service';
import { CurrentFacilityService } from './current-facility.service';
import { CustomerService } from './customer.service';
import { JwtHelperService } from './jwt-helper.service';
import { KeycloakHandlerService } from './keycloak/keycloak.service';
import { ResetInformationService } from './reset-information.service';
import { SessionWorkerService } from './session-worker';

@Injectable({
    providedIn: 'root',
})
export class SessionService implements OnDestroy {
    private source: string | null;
    private unsubscribe$: Subject<void>;
    private authenticatedCustomerTypes = [CustomersType.SALESFORCE_AUTHENTICATED, CustomersType.BUSINESS];

    constructor(
        private authService: AuthService,
        private customerService: CustomerService,
        private currentFacilityService: CurrentFacilityService,
        private tokenService: TokenService,
        private injector: Injector,
        private sessionWorkerService: SessionWorkerService,
        private matDialog: MatDialog,
        private activatedRoute: ActivatedRoute,
        private jwtHelperService: JwtHelperService,
        private configService: ConfigService,
        private keycloakHandlerService: KeycloakHandlerService,
        private loggerService: LoggerService
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.subscribeUpdateTokens();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    logout(force = false): Observable<string> {
        return this.logoutIfNeeded(force).pipe(
            switchMap(() =>
                this.signInAsAGuest().pipe(
                    map(() => {
                        return 'Complete';
                    })
                )
            ),
            switchMap(() => this.keycloakHandlerService.logoutIfAuthenticatedOnKeycloak())
        );
    }

    logoutIfNeeded(force: boolean): Observable<string> {
        return of(this.isAuthenticated()).pipe(
            switchMap((isUserLogged) => {
                if (!isUserLogged) {
                    return of('Complete');
                }

                return this.tokenService.getRefreshToken$().pipe(
                    take(1),
                    switchMap((refreshToken) => {
                        return this.logoutCargosprintUser(refreshToken);
                    }),
                    switchMap(() => {
                        return this.logoutAndResetInformation(force);
                    })
                );
            })
        );
    }

    logoutCargosprintUser(refreshToken: string): Observable<LogoutResponse> {
        const isTokenExpired = this.jwtHelperService.isTokenExpired(refreshToken);
        return this.authService.logoutCargosprintUser(refreshToken, isTokenExpired);
    }

    logoutAndResetInformation(force: boolean): Observable<string> {
        const resetInformation = this.injector.get(ResetInformationService);
        this.matDialog.closeAll();
        return resetInformation.resetUserInformation(force);
    }

    signInAsAGuest(): Observable<CredentialsTokensType> {
        return this.sessionWorkerService.requestTokens().pipe(
            switchMap(({ tokens: workerTokens, isRequestAllowed }) => {
                if (!workerTokens && isRequestAllowed) {
                    this.source = this.currentFacilityService.getFacilitySource();

                    if (this.source) {
                        return this.authService.signInAsAGuest(this.source).pipe(
                            retry({ count: 3, delay: 10000 }),
                            map((tokens: CredentialsTokensType) => {
                                this.postAndUpdateConfigTokens(tokens);
                                return tokens;
                            })
                        );
                    }

                    return throwError(() => Exception.fromJson({ description: 'Source is not defined' }));
                } else if (workerTokens) {
                    this.updateConfigTokens(workerTokens);
                    return of(workerTokens);
                }

                return throwError(() => Exception.fromJson({ description: 'No workerTokens' }));
            }),
            catchError((error) => {
                try {
                    this.loggerService.captureException(new Error(error?.description || 'Error signInAsAGuest'), {
                        tags: { signInAsAGuest: true },
                        extra: {
                            ...error,
                        },
                    });
                } catch (error) {
                    // No action needed
                }

                return throwError(() => Exception.fromJson({}));
            })
        );
    }

    signInAsCargoSprint(
        username: string,
        password: string,
        source?: string
    ): Observable<LoginAsCargoSprintUserResponse> {
        this.source = this.currentFacilityService.getFacilitySource();

        const guestToken: number | null = this.getGuestTokenToTransferCart();

        return this.authService
            .loginAsCargoSprintUser(username, password, source || this.source || '', guestToken || undefined)
            .pipe(
                map((loginAsCargoSprintUserResponse: LoginAsCargoSprintUserResponse) => {
                    if ('authorizationToken' in loginAsCargoSprintUserResponse) {
                        this.postAndUpdateConfigTokens({
                            authorizationToken: loginAsCargoSprintUserResponse.authorizationToken || '',
                            refreshToken: loginAsCargoSprintUserResponse.refreshToken || '',
                        });
                    }

                    return loginAsCargoSprintUserResponse;
                })
            );
    }

    getGuestTokenToTransferCart(): number | null {
        if (this.configService.isTransferCartEnable()) {
            return this.customerService.authorization_token_id;
        }

        return null;
    }

    refreshToken(): Observable<string> {
        return this.sessionWorkerService.isTokenRefreshAllowed().pipe(
            delay(2000),
            switchMap(({ isRequestAllowed }) => {
                if (isRequestAllowed && !this.validateToken()) {
                    return this.authService.refreshTokenRequest(this.tokenService.instant_refresh_token).pipe(
                        map((tokens: CredentialsTokensType) => {
                            this.postAndUpdateConfigTokens(tokens);
                            return tokens.authorizationToken;
                        }),
                        catchError((error) => {
                            this.postAndUpdateConfigTokens(null);
                            Swal.fire({
                                title: 'Error!',
                                text: error?.description || 'Something went wrong!',
                                icon: 'error',
                            });
                            return throwError(() => new Error('Refresh token error'));
                        })
                    );
                }

                return of('');
            })
        );
    }

    validateToken(): boolean {
        const tokenExpDate = dayjs.unix(this.tokenService.getDecodedToken().exp);
        return tokenExpDate.diff(dayjs(), 'minute') >= 13;
    }

    updateConfigTokens(tokens: CredentialsTokensType | null): void {
        if (tokens) {
            this.tokenService.setAuthTokens(tokens);
        } else {
            this.resetSession();
        }
    }

    postAndUpdateConfigTokens(tokens: CredentialsTokensType | null): void {
        this.updateConfigTokens(tokens);
        this.sessionWorkerService.postTokens(tokens);
    }

    hasToken(): boolean {
        return !!this.tokenService.instant_token;
    }

    hasToken$(): Observable<boolean> {
        return this.tokenService.getCurrentToken$().pipe(
            map((token) => !!token),
            distinctUntilChanged()
        );
    }

    isAuthenticated(): boolean {
        return this.authenticatedCustomerTypes.includes(this.customerService.customer_type);
    }

    isAuthenticated$(): Observable<boolean> {
        return this.tokenService.getCurrentToken$().pipe(
            map(() => {
                return this.authenticatedCustomerTypes.includes(this.customerService?.customer_type);
            }),
            distinctUntilChanged()
        );
    }

    subscribeUpdateTokens(): void {
        this.sessionWorkerService.updateTokens$
            .pipe(
                map((tokens) => {
                    if (tokens) {
                        this.updateConfigTokens(tokens);
                    }
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    resetSession(): void {
        this.tokenService.resetAuthTokens();
    }

    getTokenStatus$(): Observable<TokenStatusType> {
        return this.tokenService.getTokenStatus$();
    }

    getToken(): Token {
        return this.tokenService.getDecodedToken();
    }

    getTokensFromParams(): Observable<CredentialsTokensType | null> {
        return this.activatedRoute.queryParams.pipe(
            switchMap((params: Params) => {
                const authorizationToken = params['access_token'];
                const refreshToken = params['refresh_token'];

                if (authorizationToken && refreshToken) {
                    return forkJoin([
                        this.authService.uncompressToken(authorizationToken).pipe(take(1)),
                        this.authService.uncompressToken(refreshToken).pipe(take(1)),
                    ]).pipe(
                        map(([authorizationTokenResponse, refreshTokenResponse]) => {
                            const authorizationToken = authorizationTokenResponse?.result || '';
                            const refreshToken = refreshTokenResponse?.result || '';

                            if (this.jwtHelperService.isTokenValid(authorizationToken)) {
                                return {
                                    authorizationToken,
                                    refreshToken,
                                };
                            }

                            return null;
                        })
                    );
                }

                return of(null);
            })
        );
    }
}
